import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { useChannelConnect } from './ChannelPicker';
import { PostPanel } from './PostPanel';
import {
  Channel,
  channelMark,
  channelName,
  DISCONNECT_SOCIAL_CHANNEL,
  lastPostProblem,
  POSTABLE,
  REACH_CHANNEL_ROWS,
  ReachPost,
  RETRY_SOCIAL_POST,
  SET_APPROVAL_MODE,
  SKIP_SOCIAL_POST,
} from './reach';

/** Platforms where one sign-in is one account, so a connected one leaves the Add list. */
const ONE_PER_SIGN_IN = new Set(['x', 'twitter', 'threads', 'tiktok']);

/**
 * Reach → Channels ("tinycms UX optimization review v6", Prototype v6): the
 * connected social accounts, each with whether the agent asks first or posts on
 * its own, and the channels that can still be added.
 *
 * Connecting: the sign-in popup comes back with the channels it can post to
 * (a Facebook sign-in may hold several Pages and their Instagram accounts);
 * the person picks, and only then do rows exist (useChannelConnect).
 * Disconnecting removes the channel's stored credentials (nothing of our
 * access is left behind) and keeps the row (is_active = false) so its posts
 * stay attached; connecting the same page again brings that row back with a
 * fresh sign-in.
 *
 * Health (Prototype v7): a channel whose last post didn't go out says so under
 * its name; Fix opens that post with why, Try again and Skip.
 */
export function ReachChannels() {
  const { accountId } = useAccount();
  const { data, refetch } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNEL_ROWS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [setMode] = useMutation(SET_APPROVAL_MODE);
  const [disconnectChannel] = useMutation(DISCONNECT_SOCIAL_CHANNEL);
  const [retry] = useMutation(RETRY_SOCIAL_POST);
  const [skip] = useMutation(SKIP_SOCIAL_POST);
  const [busy, setBusy] = useState<string | null>(null);
  const [fixing, setFixing] = useState<ReachPost | null>(null);
  const { start, picker } = useChannelConnect({
    accountId,
    onConnected: () => void refetch(),
  });

  const channels = (data?.socialProfiles || []).filter((c) => c.isActive);
  const connectedPlatforms = new Set(channels.map((c) => c.platform.toLowerCase()));

  const changeMode = async (c: Channel, mode: 'ask' | 'auto') => {
    if (c.approvalMode === mode) return;
    setBusy(c.id);
    try {
      await setMode({ variables: { id: c.id, mode } });
      await refetch();
      showInkToast(
        mode === 'ask'
          ? `${channelName(c.platform)}: your agent asks you first.`
          : `${channelName(c.platform)}: your agent posts on its own.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (c: Channel) => {
    if (
      !window.confirm(
        `Disconnect ${channelName(c.platform)}? Your sign-in is removed and nothing more is posted there until you connect it again.`
      )
    )
      return;
    setBusy(c.id);
    try {
      await disconnectChannel({ variables: { id: c.id } });
      await refetch();
      showInkToast(`${channelName(c.platform)} disconnected. Your sign-in has been removed.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const fix = async (post: ReachPost, kind: 'approve' | 'skip' | 'retry') => {
    if (kind === 'approve') return;
    setBusy(post.id);
    try {
      if (kind === 'retry') {
        await retry({ variables: { id: post.id } });
        showInkToast('Trying again. It goes out in a minute.');
      } else {
        await skip({ variables: { id: post.id } });
        showInkToast('Skipped. It won’t go out.');
      }
      setFixing(null);
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const addable = POSTABLE.filter(
    (p) =>
      !ONE_PER_SIGN_IN.has(p) ||
      !(connectedPlatforms.has(p) || (p === 'x' && connectedPlatforms.has('twitter')))
  );

  return (
    <div className="rch">
      <section>
        <h2 className="rch-h">Connected</h2>
        <p className="ink-muted rch-sub">
          For each channel, choose whether your agent asks you first or posts on its own.
        </p>
        {channels.length ? (
          <div className="rch-list">
            {channels.map((c) => {
              const problem = lastPostProblem(c);
              return (
                <div className="rch-row" key={c.id}>
                  <span className="rc-mark">{channelMark(c.platform)}</span>
                  <div className="rch-name">
                    <div>{channelName(c.platform)}</div>
                    <div className="ink-muted">
                      {c.profileUsername
                        ? `@${c.profileUsername.replace(/^@/, '')}`
                        : c.profileName || ''}
                    </div>
                    {problem && c.lastPost ? (
                      <div className="rch-problem">
                        {problem} ·{' '}
                        <button type="button" onClick={() => setFixing(c.lastPost || null)}>
                          Fix
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <span
                    className="ink-seg rch-seg"
                    role="radiogroup"
                    aria-label={`${channelName(c.platform)} approvals`}
                  >
                    {(
                      [
                        ['ask', 'Ask me first'],
                        ['auto', 'Post on its own'],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={(c.approvalMode || 'ask') === mode}
                        data-active={(c.approvalMode || 'ask') === mode ? 'true' : 'false'}
                        disabled={busy === c.id}
                        onClick={() => void changeMode(c, mode)}
                      >
                        {label}
                      </button>
                    ))}
                  </span>
                  <button
                    type="button"
                    className="ink-quiet-link"
                    disabled={busy === c.id}
                    onClick={() => void disconnect(c)}
                  >
                    Disconnect
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="rch-none">No channels connected yet.</p>
        )}
      </section>

      <section style={{ marginTop: 36 }}>
        <h2 className="rch-h">Add a channel</h2>
        <div className="rch-add">
          {addable.map((p) => (
            <button
              key={p}
              type="button"
              className="rch-add-btn"
              disabled={!accountId}
              onClick={() => void start(p)}
            >
              <span className="rc-mark rc-mark-sm">{channelMark(p)}</span>
              {channelName(p)}
              {connectedPlatforms.has(p) ? <span className="ink-muted"> · add another</span> : null}
            </button>
          ))}
        </div>
        <p className="ink-muted rch-sub" style={{ marginTop: 10 }}>
          You’ll sign in to the channel in a new window. One Facebook sign-in covers your Pages and
          the Instagram accounts linked to them, and a LinkedIn sign-in covers your profile and the
          Pages you administer; you pick which to connect.
        </p>
      </section>

      {picker}
      {fixing ? (
        <PostPanel
          post={fixing}
          busy={busy === fixing.id}
          onClose={() => setFixing(null)}
          onAct={(kind) => void fix(fixing, kind)}
        />
      ) : null}
    </div>
  );
}
